import React, { useState, useEffect } from 'react';
import './TrustmarkPricing.css';
import { Check, ArrowRight, Shield, Zap, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Helmet } from 'react-helmet';

// Define SKU constants
const SKU_IDS = {
  BASIC: 'BASIC_PLAN',
  BUSINESS: 'BUSINESS_PLAN',
  ENTERPRISE: 'ENTERPRISE_PLAN'
};

const TrustmarkPricing = () => {
  const [stripePromise, setStripePromise] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchStripeKey = async () => {
      try {
        const response = await fetch('/config/stripe');
        if (!response.ok) throw new Error('Failed to fetch Stripe publishable key');
        const { publishableKey } = await response.json();
        const stripe = await loadStripe(publishableKey);
        setStripePromise(stripe);
      } catch (error) {
        console.error('Error fetching Stripe publishable key:', error);
      }
    };
    fetchStripeKey();
  }, []);

  const checkAuthenticated = async () => {
    try {
      const resp = await fetch('/api/profile', {
        method: 'GET',
        credentials: 'include',
      });
      return resp.ok;
    } catch (err) {
      console.error('Error checking authentication:', err);
      return false;
    }
  };

  // New function to check for an active subscription
  const checkActiveSubscription = async (skuId) => {
    try {
      const response = await fetch(`/api/check-active-subscription?skuId=${skuId}`, {
        method: 'GET',
        credentials: 'include',
      });
      const data = await response.json();
      if (!response.ok || data.active) {
        // If the response is not OK or an active subscription exists,
        // display an error message.
        setErrorMessage(data.message || 'Active subscription exists.');
        setIsErrorModalOpen(true);
        return false;
      }
      return true;
    } catch (error) {
      console.error('Error checking active subscription:', error);
      setErrorMessage('An error occurred while checking your subscription.');
      setIsErrorModalOpen(true);
      return false;
    }
  };
  
  // Handler for Basic Plan using BASIC_PLAN SKU
  const handleBasicSelect = async () => {
    const authenticated = await checkAuthenticated();
    if (!authenticated) {
      navigate('/signin', { state: { target: '/pricing', sku: SKU_IDS.BASIC } });
      return;
    }
    // Pass the BASIC_PLAN SKU to check for an active subscription
    const noActiveSubscription = await checkActiveSubscription(SKU_IDS.BASIC);
    if (!noActiveSubscription) return;
    
    // Redirect to checkout for the paid Basic plan:
    redirectToCheckout(SKU_IDS.BASIC);
  };
  

  // Handler for Business Plan using BUSINESS_PLAN SKU
  const handleBusinessSelect = async () => {
    const authenticated = await checkAuthenticated();
    if (!authenticated) {
      navigate('/signin', { state: { target: '/pricing', sku: SKU_IDS.BUSINESS } });
      return;
    }
    // Check for an active subscription before proceeding
    const noActiveSubscription = await checkActiveSubscription(SKU_IDS.BUSINESS);
    if (!noActiveSubscription) return;
    
    // Redirect to checkout for the paid Basic plan:
    redirectToCheckout(SKU_IDS.BUSINESS);
  };

  // Handler for Enterprise Plan using ENTERPRISE_PLAN SKU
  // If enterprise subscriptions are handled online, you can call redirectToCheckout(SKU_IDS.ENTERPRISE)
  // For this example, we'll continue to navigate to a contact page.
  const handleEnterpriseSelect = async () => {
    const authenticated = await checkAuthenticated();
    if (!authenticated) {
      navigate('/signin', { state: { target: '/pricing', sku: SKU_IDS.ENTERPRISE } });
      return;
    }
    navigate('/contact');
  };

  const updateSubscription = async (skuId) => {
    try {
      const response = await fetch('/api/update-subscription', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ skuId }),
      });

      if (!response.ok) {
        setErrorMessage('Error updating subscription.');
        setIsErrorModalOpen(true);
        return false;
      } else {
        return true;
      }
    } catch (error) {
      console.error('Error updating subscription:', error);
      setErrorMessage('An error occurred while updating your subscription.');
      setIsErrorModalOpen(true);
      return false;
    }
  };

  const redirectToCheckout = async (skuId) => {
    try {
      if (!stripePromise) return;
      const stripe = await stripePromise;
      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ skuId }),
      });

      if (!response.ok) {
        setErrorMessage('Error creating checkout session.');
        setIsErrorModalOpen(true);
        return;
      }
      const session = await response.json();
      await stripe.redirectToCheckout({ sessionId: session.id });
    } catch (error) {
      console.error('Error redirecting to checkout:', error);
    }
  };

  const handleCloseErrorModal = () => setIsErrorModalOpen(false);
  const handleCloseSuccessModal = () => setIsSuccessModalOpen(false);

  const plans = [
    {
      name: 'Basic Plan',
      price: '$25',
      period: '/month',
      description: 'Perfect for small websites',
      icon: Shield,
      features: [
        '10,000 verifications per month',
        'Basic bot protection with shape-drawing captcha',
        'Limited analytics (success/failure rates)',
        'Community support via forums and documentation'
      ],
      buttonText: 'Get Started',
      handler: handleBasicSelect,
      popular: false
    },
    {
      name: 'Business Plan',
      price: '$99',
      period: '/month',
      description: 'Most popular for growing businesses',
      icon: Zap,
      features: [
        '100,000 verifications per month',
        'Advanced bot protection with AI-driven threat detection',
        'Enhanced analytics (user behaviour, bot attempts, and success rates)',
        'Priority email support with a 24-hour response time'
      ],
      buttonText: 'Get Started',
      handler: handleBusinessSelect,
      popular: true
    },
    {
      name: 'Enterprise Plan',
      price: 'Contact Us',
      period: '',
      description: 'For large organizations',
      icon: Users,
      features: [
        'Unlimited verifications with scalable infrastructure',
        'Enterprise support',
        'Enterprise-grade security with real-time threat intelligence and custom rules',
        'Comprehensive analytics dashboard with API access for integration',
        'Dedicated account manager and 24/7 premium support'
      ],
      buttonText: 'Contact Sales',
      handler: handleEnterpriseSelect,
      popular: false
    }
  ];

  return (
    <div className="trustmark-pricing-container">
      <Helmet>
        <title>TrustMark Pricing Plans</title>
        <meta
          name="description"
          content="Choose the right plan for you—Basic, Business, or Enterprise— with TrustMark security solutions."
        />
      </Helmet>

      {/* Hero Section */}
      <section className="trustmark-pricing-hero">
        <div className="trustmark-pricing-hero-content">
          <h1 className="trustmark-pricing-hero-title">
            TrustMark <span className="trustmark-pricing-hero-accent">Pricing Plans</span>
          </h1>
          <p className="trustmark-pricing-hero-subtitle">
            Choose the best security plan for your needs
          </p>
        </div>
      </section>

      {/* Pricing Cards Section */}
      <section className="trustmark-pricing-cards-section">
        <div className="trustmark-pricing-section-wrapper">
          <div className="trustmark-pricing-cards-grid">
            {plans.map((plan, index) => {
              const IconComponent = plan.icon;
              return (
                <div
                  key={index}
                  className={`trustmark-pricing-card ${plan.popular ? 'trustmark-pricing-card-popular' : ''}`}
                >
                  {plan.popular && (
                    <div className="trustmark-pricing-popular-badge">
                      Most Popular
                    </div>
                  )}
                  
                  <div className="trustmark-pricing-card-header">
                    <div className="trustmark-pricing-card-icon">
                      <IconComponent strokeWidth={1.5} />
                    </div>
                    <h3 className="trustmark-pricing-card-title">{plan.name}</h3>
                    <p className="trustmark-pricing-card-description">{plan.description}</p>
                    <div className="trustmark-pricing-card-price">
                      <span className="trustmark-pricing-price-amount">{plan.price}</span>
                      {plan.period && <span className="trustmark-pricing-price-period">{plan.period}</span>}
                    </div>
                  </div>

                  <div className="trustmark-pricing-card-features">
                    <ul className="trustmark-pricing-features-list">
                      {plan.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="trustmark-pricing-feature-item">
                          <Check className="trustmark-pricing-feature-icon" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="trustmark-pricing-card-footer">
                    <button
                      className={`trustmark-pricing-card-button ${plan.popular ? 'trustmark-pricing-button-primary' : 'trustmark-pricing-button-secondary'}`}
                      onClick={plan.handler}
                    >
                      {plan.buttonText}
                      <ArrowRight className="trustmark-pricing-button-icon" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Error Modal */}
      {isErrorModalOpen && (
        <div className="trustmark-pricing-modal-overlay">
          <div className="trustmark-pricing-modal">
            <div className="trustmark-pricing-modal-content trustmark-pricing-error-modal">
              <h3 className="trustmark-pricing-modal-title">Error</h3>
              <p className="trustmark-pricing-modal-message">{errorMessage}</p>
              <button
                className="trustmark-pricing-modal-button"
                onClick={handleCloseErrorModal}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {isSuccessModalOpen && (
        <div className="trustmark-pricing-modal-overlay">
          <div className="trustmark-pricing-modal">
            <div className="trustmark-pricing-modal-content trustmark-pricing-success-modal">
              <div className="trustmark-pricing-success-icon">
                <Check />
              </div>
              <h3 className="trustmark-pricing-modal-title">Success!</h3>
              <p className="trustmark-pricing-modal-message">
                Check your email for registration details.
              </p>
              <button
                className="trustmark-pricing-modal-button"
                onClick={handleCloseSuccessModal}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrustmarkPricing;